import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Heading,
  Text,
  FormControl,
  FormLabel,
  Input,
  Button,
  HStack,
  VStack,
  Grid,
  GridItem,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  useColorModeValue,
  Spinner,
  Center,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
} from "@chakra-ui/react";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie,
} from "recharts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBoxOpen, faCalendarXmark, faMoneyBillWave, faSyringe, faVial } from "@fortawesome/free-solid-svg-icons";
import * as IconButton from "../../themes/IconButtonSet";
import apiService from "../../services/ApiService";
import { useAuth } from "../../context/AuthContext";
import { StandardPageTitle, StandardHeader } from "../../themes/StandardHeader";

const VaccineInventoryDashboard = () => {
  const { sessionId } = useAuth();
  const [inventoryData, setInventoryData] = useState([]);
  const [allRegions, setAllRegions] = useState([]); // เก็บรายการ Region ทั้งหมด
  const [expiringVaccines, setExpiringVaccines] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  
  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [inventoryDetails, setInventoryDetails] = useState([]);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [modalFilter, setModalFilter] = useState({
    warehouseName: "",
  });

  const cardBg = useColorModeValue("white", "gray.800");

  // ฟังก์ชันดึงรายการ Region ทั้งหมด (Central และ Region)
  const fetchAllRegions = async () => {
    try {
      // ดึง Warehouse ทั้งหมดก่อน แล้วกรองใน frontend
      const response = await apiService({
        method: "POST",
        url: "/getVaccineWarehousesList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        // กรองเฉพาะ WarehouseType = 'Central' หรือ 'Region'
        const regions = (response.VaccineWarehousesList || []).filter(
          wh => wh.WarehouseType === 'Central' || wh.WarehouseType === 'Region'
        );
        // เรียงตาม WarehouseID
        regions.sort((a, b) => (a.WarehouseID || '').localeCompare(b.WarehouseID || ''));
        console.log("All regions fetched:", regions);
        setAllRegions(regions);
      } else {
        console.error("Error fetching regions:", response.error);
        setAllRegions([]);
      }
    } catch (error) {
      console.error("Error fetching regions:", error);
      setAllRegions([]);
    }
  };

  // ฟังก์ชันดึงข้อมูลยอดคงคลัง
  const fetchInventoryData = async () => {
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/getInventoryByRegion",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        console.log("Inventory data received:", response.InventoryList);
        setInventoryData(response.InventoryList || []);
      } else {
        console.error("Error fetching inventory data:", response.error);
        setInventoryData([]);
      }
    } catch (error) {
      console.error("Error fetching inventory data:", error);
      setInventoryData([]);
    } finally {
      setIsLoading(false);
    }
  };

  // ฟังก์ชันดึงรายละเอียด Warehouse ใน RegionID
  const handleViewInventoryDetails = async (regionID) => {
    setIsLoadingDetails(true);
    setIsModalOpen(true);
    
    try {
      // ถ้า RegionID เป็น NULL หรือ N/A ให้ใช้ 'WHH100' แทน
      const actualRegionID = (!regionID || regionID === 'N/A' || regionID === null || regionID === undefined) 
        ? 'WHH100' 
        : regionID;
      
      const response = await apiService({
        method: "POST",
        url: "/getInventoryByRegionDetails",
        data: { RegionID: actualRegionID },
        sessionId: sessionId || "",
      });
      
      if (response.result === "success") {
        // ถ้า RegionID เป็น 'N/A' หรือ null ให้แสดง "สำนักเทคโนโลยีชีวภัณฑ์สัตว์"
        const displayRegionName = (actualRegionID === 'WHH100' || regionID === 'N/A' || regionID === null || regionID === undefined)
          ? 'สำนักเทคโนโลยีชีวภัณฑ์สัตว์'
          : (response.RegionName || actualRegionID);
        setModalTitle(`รายละเอียดยอดคงคลัง - ${displayRegionName}`);
        setInventoryDetails(response.DetailsList || []);
        setModalFilter({ warehouseName: "" });
      } else {
        alert("ไม่พบข้อมูล: " + response.error);
        setInventoryDetails([]);
      }
    } catch (error) {
      console.error("Error fetching inventory details:", error);
      alert("เกิดข้อผิดพลาดในการดึงข้อมูล");
      setInventoryDetails([]);
    } finally {
      setIsLoadingDetails(false);
    }
  };

  // ฟังก์ชันดึงรายละเอียดทั้งหมด (ทุก Region)
  const handleViewAllInventoryDetails = async () => {
    setIsLoadingDetails(true);
    setIsModalOpen(true);
    setModalTitle("รายละเอียดยอดคงคลังทั้งหมด (ทุก Region)");
    setModalFilter({ warehouseName: "" });
    
    try {
      // ใช้ข้อมูลจาก inventoryData ที่มีอยู่แล้ว
      // จัดกลุ่มตาม Region และ Warehouse
      const groupedData = {};
      
      if (inventoryData && inventoryData.length > 0) {
        // หา Central warehouse
        const centralWarehouse = allRegions && allRegions.length > 0
          ? allRegions.find(r => r.WarehouseType === 'Central')
          : null;
        const centralWarehouseID = centralWarehouse ? centralWarehouse.WarehouseID : null;

        inventoryData.forEach(item => {
          // หา RegionID ที่ถูกต้อง
          // Backend จะส่ง RegionID มาเป็น:
          // - 'N/A' สำหรับ Central (RegionID = NULL)
          // - 'REGION1', 'REGION2', ... สำหรับ Region/Province
          let regionID = item.RegionID;
          let targetRegionID = null;
          
          if (!regionID || regionID === '' || regionID === 'N/A' || regionID === 'n/a') {
            // Central warehouse - ใช้ 'WHH100' แทน
            targetRegionID = 'WHH100';
          } else {
            // หา Region warehouse ที่มี RegionID ตรงกัน (ไม่ใช่ WarehouseID)
            const matchedRegion = allRegions && allRegions.length > 0
              ? allRegions.find(r => r.RegionID === regionID && r.WarehouseType === 'Region')
              : null;
            if (matchedRegion) {
              targetRegionID = matchedRegion.WarehouseID; // ใช้ WarehouseID เป็น key
            } else {
              // ถ้าไม่เจอ ให้ใช้ 'WHH100'
              targetRegionID = 'WHH100';
            }
          }

          // สร้าง key สำหรับแต่ละ Region
          if (!groupedData[targetRegionID]) {
            const regionInfo = allRegions && allRegions.length > 0
              ? allRegions.find(r => r.WarehouseID === targetRegionID)
              : null;
            
            // หา RegionName
            let regionName = item.RegionName;
            if (!regionName || regionName === null) {
              if (regionID === 'N/A' || targetRegionID === 'WHH100') {
                regionName = 'สำนักเทคโนโลยีชีวภัณฑ์สัตว์';
              } else {
                regionName = regionInfo ? regionInfo.WarehouseName : (item.RegionName || regionID);
              }
            }
            
            groupedData[targetRegionID] = {
              RegionID: regionID === 'N/A' ? null : regionID, // เก็บ RegionID เดิมไว้
              RegionName: regionName,
              warehouses: {},
            };
          }

          // ใช้ WarehouseID จาก inventoryData (ถ้ามี) หรือใช้ RegionID
          // เนื่องจากข้อมูลจาก getInventoryByRegion อาจไม่มี WarehouseID แยก
          // ให้ใช้ RegionID เป็น key สำหรับ warehouse
          const warehouseKey = item.WarehouseID || regionID;
          if (!groupedData[targetRegionID].warehouses[warehouseKey]) {
            groupedData[targetRegionID].warehouses[warehouseKey] = {
              WarehouseID: warehouseKey,
              WarehouseName: item.WarehouseName || item.RegionName || warehouseKey,
              vaccines: [],
            };
          }

          // เพิ่มข้อมูลวัคซีน
          groupedData[targetRegionID].warehouses[warehouseKey].vaccines.push({
            VaccineID: item.VaccineID,
            VaccineNameTH: item.VaccineNameTH,
            VaccineNameEN: item.VaccineNameEN,
            TotalBottles: item.TotalBottles || 0,
            TotalDoses: item.TotalDoses || 0,
            TotalAmount: item.TotalAmount || 0,
          });
        });
      }

      // แปลงเป็น array สำหรับแสดงใน Modal
      // จัดรูปแบบให้เหมือนกับ getInventoryByRegionDetails
      const detailsList = [];
      Object.values(groupedData).forEach(region => {
        Object.values(region.warehouses).forEach(warehouse => {
          warehouse.vaccines.forEach(vaccine => {
            detailsList.push({
              WarehouseID: warehouse.WarehouseID,
              WarehouseName: warehouse.WarehouseName,
              RegionID: region.RegionID,
              RegionName: region.RegionName,
              VaccineID: vaccine.VaccineID,
              VaccineNameTH: vaccine.VaccineNameTH,
              VaccineNameEN: vaccine.VaccineNameEN,
              TotalBottles: vaccine.TotalBottles,
              TotalDoses: vaccine.TotalDoses,
              TotalAmount: vaccine.TotalAmount,
            });
          });
        });
      });

      setInventoryDetails(detailsList);
    } catch (error) {
      console.error("Error preparing all inventory details:", error);
      alert("เกิดข้อผิดพลาดในการเตรียมข้อมูล");
      setInventoryDetails([]);
    } finally {
      setIsLoadingDetails(false);
    }
  };

  // ฟังก์ชันดึงข้อมูลวัคซีนที่ใกล้หมดอายุ
  const fetchExpiringVaccines = async () => {
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/getExpiringVaccines",
        data: {},
        sessionId: sessionId || "",
      });
            if (response.result === "success") {
              console.log("Expiring vaccines data received:", response.ExpiringVaccinesList);
              console.log("First item sample:", response.ExpiringVaccinesList?.[0]);
              setExpiringVaccines(response.ExpiringVaccinesList || []);
            } else {
              console.error("Error fetching expiring vaccines:", response.error);
              setExpiringVaccines([]);
            }
    } catch (error) {
      console.error("Error fetching expiring vaccines:", error);
      setExpiringVaccines([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (sessionId) {
      fetchAllRegions();
      fetchInventoryData();
      fetchExpiringVaccines();
    }
  }, [sessionId]);

  // ฟังก์ชันจัดกลุ่มวัคซีนตาม VaccineID
  const getVaccineGroup = (vaccineID) => {
    if (!vaccineID) return null;
    
    // วัคซีนโรคปากและเท้าเปื่อย โค-กระบือ
    if (['VAX1005', 'VAX1006', 'VAX1007', 'VAX1008', 'VAX1009', 'VAX1010', 'VAX1011'].includes(vaccineID)) {
      return 'โรคปากและเท้าเปื่อย โค-กระบือ';
    }
    
    // วัคซีนโรคปากและเท้าเปื่อยสุกร
    if (['VAX2003', 'VAX2004', 'VAX2005', 'VAX2006'].includes(vaccineID)) {
      return 'โรคปากและเท้าเปื่อยสุกร';
    }
    
    // อหิวาห์สุกร
    if (['VAX2001', 'VAX2002'].includes(vaccineID)) {
      return 'อหิวาห์สุกร';
    }
    
    return null; // ไม่ได้อยู่ในกลุ่มที่ต้องรวม
  };

  // ฟังก์ชันกำหนดสีให้กับวัคซีนแต่ละชนิด (ให้สีเดียวกันทุกกราฟ)
  const getVaccineColor = (vaccineName, index) => {
    // สีสำหรับกลุ่มที่รวมแล้ว
    const groupColors = {
      'โรคปากและเท้าเปื่อย โค-กระบือ': '#E53E3E',
      'โรคปากและเท้าเปื่อยสุกร': '#3182CE',
      'อหิวาห์สุกร': '#805AD5',
    };
    
    if (groupColors[vaccineName]) {
      return groupColors[vaccineName];
    }
    
    // สีสำหรับวัคซีนอื่นๆ (ใช้สีที่แตกต่างกัน)
    const defaultColors = [
      '#38A169', '#D69E2E', '#F56565', '#9F7AEA', '#ED8936',
      '#48BB78', '#ECC94B', '#FC8181', '#B794F4', '#F6AD55',
      '#68D391', '#F6E05E', '#FEB2B2', '#D6BCFA', '#FBD38D',
    ];
    
    return defaultColors[index % defaultColors.length];
  };

  // แปลงข้อมูล inventory เป็นรูปแบบที่ใช้ในกราฟ
  const inventoryChartData = useMemo(() => {
    const vaccineSet = new Set();
    const regionMap = {};

    // หา Central warehouse (WarehouseType = 'Central') - ใช้จาก allRegions ถ้ามี
    const centralWarehouse = allRegions && allRegions.length > 0 
      ? allRegions.find(r => r.WarehouseType === 'Central')
      : null;
    const centralWarehouseID = centralWarehouse ? centralWarehouse.WarehouseID : 'WHH100'; // fallback

    // สร้าง regionSet จาก inventoryData โดยตรง (ไม่พึ่งพา allRegions เพราะอาจมีสิทธิ์จำกัด)
    const regionSet = new Set();
    if (inventoryData && inventoryData.length > 0) {
      inventoryData.forEach(item => {
        const regionID = item.RegionID;
        const regionKey = (!regionID || regionID === '' || regionID === 'N/A' || regionID === 'n/a') 
          ? (centralWarehouseID || 'N/A')
          : regionID;
        regionSet.add(regionKey);
      });
    }

    // จัดกลุ่มข้อมูล inventory ตาม RegionID และ VaccineID
    // ข้อมูลจาก backend จะมี RegionID ที่เป็น:
    // - 'N/A' → Central (WarehouseType = 'Central', RegionID = NULL)
    // - 'REGION1', 'REGION2', ... → Region (WarehouseType = 'Region', RegionID = REGION1, REGION2, ...)
    // Backend จะรวมข้อมูลจาก Region warehouse + Province warehouses ที่มี RegionID เดียวกัน
    // ใช้ RegionID จาก inventoryData โดยตรง (ไม่ต้องพึ่งพา allRegions เพราะอาจมีสิทธิ์จำกัด)
    if (inventoryData && inventoryData.length > 0) {
      inventoryData.forEach(item => {
        // ใช้ RegionID จาก inventoryData โดยตรง
        const regionID = item.RegionID;
        const regionKey = (!regionID || regionID === '' || regionID === 'N/A' || regionID === 'n/a') 
          ? 'WHH100'
          : regionID;

        // ถ้ายังไม่มี regionKey ใน regionMap ให้สร้างใหม่
        if (!regionMap[regionKey]) {
          // หา Region name จาก inventoryData โดยตรง (ไม่พึ่งพา allRegions)
          let regionName = item.RegionName;
          
          // ถ้าไม่มี RegionName ใน inventoryData ให้ใช้ค่า default
          if (!regionName || regionName === null) {
            if (regionKey === 'WHH100') {
              // Central warehouse
              regionName = 'สำนักเทคโนโลยีชีวภัณฑ์สัตว์';
            } else {
              // Region warehouse - หาชื่อ Region จาก inventoryData ที่มี RegionID ตรงกัน
              const regionItem = inventoryData.find(d => d.RegionID === regionID && d.RegionName);
              regionName = regionItem ? regionItem.RegionName : `Region ${regionID}`;
            }
          }

          // หา order - Central = 0, Region อื่นๆ = 1, 2, 3, ... (เรียงตาม RegionID)
          let order = 9999;
          if (regionKey === 'WHH100') {
            order = 0; // Central อยู่ลำดับแรก
          } else {
            // เรียง Region ตาม RegionID (REGION1, REGION2, ...)
            const regionNum = parseInt(regionID.replace('REGION', '')) || 9999;
            order = regionNum;
          }

          regionMap[regionKey] = {
            RegionID: regionKey, // เก็บ 'WHH100' สำหรับ Central
            RegionName: regionName,
            vaccines: {},
            order: order,
          };
        }

        const vaccineName = getVaccineGroup(item.VaccineID) || item.VaccineNameTH || item.VaccineNameEN || 'Unknown';
        vaccineSet.add(vaccineName);

        if (!regionMap[regionKey].vaccines[vaccineName]) {
          regionMap[regionKey].vaccines[vaccineName] = {
            VaccineNameTH: vaccineName,
            TotalDoses: 0,
            TotalAmount: 0,
          };
        }

        regionMap[regionKey].vaccines[vaccineName].TotalDoses += Number(item.TotalDoses || 0);
        regionMap[regionKey].vaccines[vaccineName].TotalAmount += Number(item.TotalAmount || 0);
      });
    }

    // สร้างข้อมูลสำหรับกราฟ
    // ใช้ข้อมูลจาก regionMap โดยตรง (ไม่พึ่งพา allRegions เพราะอาจมีสิทธิ์จำกัด)
    let chartData = [];
    
    // แปลง regionMap เป็น array และเรียงตาม order
    const regionArray = Object.values(regionMap).sort((a, b) => a.order - b.order);
    
    chartData = regionArray.map(regionData => {
      const dataPoint = {
        regionID: regionData.RegionID || 'WHH100', // ใช้ 'WHH100' สำหรับ Central
        regionName: regionData.RegionName,
      };

      // เพิ่มข้อมูลแต่ละวัคซีน (ถ้ามี)
      Object.keys(regionData.vaccines).forEach(vaccineName => {
        const vaccine = regionData.vaccines[vaccineName];
        dataPoint[`${vaccineName}_doses`] = vaccine.TotalDoses;
        dataPoint[`${vaccineName}_amount`] = vaccine.TotalAmount;
      });

      // สำหรับวัคซีนที่ไม่มีข้อมูล ให้ตั้งค่าเป็น 0
      Array.from(vaccineSet).forEach(vaccineName => {
        if (dataPoint[`${vaccineName}_doses`] === undefined) {
          dataPoint[`${vaccineName}_doses`] = 0;
        }
        if (dataPoint[`${vaccineName}_amount`] === undefined) {
          dataPoint[`${vaccineName}_amount`] = 0;
        }
      });

      return dataPoint;
    });

    const result = {
      chartData,
      vaccineNames: Array.from(vaccineSet).sort(),
    };
    
    console.log('inventoryChartData result:', result);
    console.log('chartData regionIDs:', chartData.map(d => ({ regionID: d.regionID, regionName: d.regionName })));
    return result;
  }, [inventoryData, allRegions]);

  // คำนวณสรุปภาพรวมของวัคซีนคงคลังทั้งหมด
  const inventorySummary = useMemo(() => {
    if (!inventoryData || inventoryData.length === 0) {
      return {
        vaccineTypes: 0,
        totalDoses: 0,
        totalAmount: 0,
      };
    }

    const vaccineSet = new Set();
    let totalDoses = 0;
    let totalAmount = 0;

    inventoryData.forEach(item => {
      const vaccineName = getVaccineGroup(item.VaccineID) || item.VaccineNameTH || item.VaccineNameEN || 'Unknown';
      vaccineSet.add(vaccineName);
      totalDoses += Number(item.TotalDoses || 0);
      totalAmount += Number(item.TotalAmount || 0);
    });

    return {
      vaccineTypes: vaccineSet.size,
      totalDoses,
      totalAmount,
    };
  }, [inventoryData]);

  // สร้างข้อมูลสำหรับกราฟภาพรวม (รวมทุกชนิดวัคซีน)
  const overallChartData = useMemo(() => {
    console.log('overallChartData - inventoryChartData:', inventoryChartData);
    console.log('overallChartData - allRegions:', allRegions);
    
    if (!inventoryChartData.chartData || inventoryChartData.chartData.length === 0) {
      // ถ้าไม่มีข้อมูล inventory แต่มี allRegions ให้แสดงทุก Region ด้วยค่า 0
      if (allRegions && allRegions.length > 0) {
        const emptyData = allRegions.map(region => ({
          regionID: region.WarehouseID,
          regionName: region.WarehouseName || region.WarehouseID,
          totalDoses: 0,
          totalAmount: 0,
        }));
        console.log('overallChartData - empty data:', emptyData);
        return emptyData;
      }
      console.log('overallChartData - returning empty array');
      return [];
    }

    const result = inventoryChartData.chartData.map(region => {
      const dataPoint = {
        regionID: region.regionID,
        regionName: region.regionName,
        totalDoses: 0,
        totalAmount: 0,
      };

      // รวมจำนวนโดสและจำนวนเงินของทุกชนิดวัคซีน
      if (inventoryChartData.vaccineNames && inventoryChartData.vaccineNames.length > 0) {
        inventoryChartData.vaccineNames.forEach(vaccineName => {
          dataPoint.totalDoses += Number(region[`${vaccineName}_doses`] || 0);
          dataPoint.totalAmount += Number(region[`${vaccineName}_amount`] || 0);
        });
      }

      return dataPoint;
    });
    
    console.log('overallChartData - result:', result);
    return result;
  }, [inventoryChartData, allRegions]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Box
          bg="white"
          p={3}
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
          boxShadow="md"
        >
          <Text fontWeight="bold" mb={2}>{label}</Text>
          {payload.map((entry, index) => (
            <Text key={index} color={entry.color} fontSize="sm">
              {entry.name}: {entry.value.toLocaleString("th-TH")}
            </Text>
          ))}
        </Box>
      );
    }
    return null;
  };

  const CustomPieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      return (
        <Box
          bg="white"
          p={3}
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
          boxShadow="md"
        >
          <Text fontWeight="bold" mb={2}>{data.name}</Text>
          <Text color={data.color} fontSize="sm">
            {data.dataKey === 'doses' 
              ? `จำนวนโดส: ${data.value.toLocaleString("th-TH")}`
              : `จำนวนเงิน: ${data.value.toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} บาท`}
          </Text>
        </Box>
      );
    }
    return null;
  };

  // Custom Label Component สำหรับ Pie Chart (แสดงนอกกราฟ)
  const CustomPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, name, percent, fill }) => {
    const RADIAN = Math.PI / 180;
    // คำนวณตำแหน่งให้อยู่นอก pie chart
    const radius = outerRadius + 20; // ระยะห่างจากขอบ pie chart
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill={fill || "#333"}
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        fontSize={12}
        fontWeight="normal"
      >
        {`${name}: ${(percent * 100).toFixed(1)}%`}
      </text>
    );
  };

  // คำนวณข้อมูล Pie Chart สำหรับแสดง 5 อันดับแรก + อื่นๆ
  const pieChartData = useMemo(() => {
    if (!inventoryData || inventoryData.length === 0) {
      return { doses: [], amounts: [] };
    }

    // รวมข้อมูลตามชนิดวัคซีน
    const vaccineMap = {};
    
    inventoryData.forEach(item => {
      const vaccineName = getVaccineGroup(item.VaccineID) || item.VaccineNameTH || item.VaccineNameEN || 'Unknown';
      const key = vaccineName;
      
      if (!vaccineMap[key]) {
        vaccineMap[key] = {
          name: key,
          doses: 0,
          amounts: 0,
        };
      }
      
      vaccineMap[key].doses += Number(item.TotalDoses || 0);
      vaccineMap[key].amounts += Number(item.TotalAmount || 0);
    });

    // แปลงเป็น array และเรียงตามยอด
    const vaccineArray = Object.values(vaccineMap);
    
    // เรียงตามจำนวนโดสหรือราคา
    const sortedByDoses = [...vaccineArray].sort((a, b) => b.doses - a.doses);
    const sortedByAmounts = [...vaccineArray].sort((a, b) => b.amounts - a.amounts);

    // เลือก 5 อันดับแรก
    const top5Doses = sortedByDoses.slice(0, 5);
    const top5Amounts = sortedByAmounts.slice(0, 5);

    // รวมส่วนที่เหลือเป็น "อื่นๆ"
    const otherDoses = sortedByDoses.slice(5).reduce((sum, item) => sum + item.doses, 0);
    const otherAmounts = sortedByAmounts.slice(5).reduce((sum, item) => sum + item.amounts, 0);

    const dosesData = [...top5Doses];
    if (otherDoses > 0) {
      dosesData.push({ name: 'อื่นๆ', doses: otherDoses, amounts: 0 });
    }

    const amountsData = [...top5Amounts];
    if (otherAmounts > 0) {
      amountsData.push({ name: 'อื่นๆ', doses: 0, amounts: otherAmounts });
    }

    return { doses: dosesData, amounts: amountsData };
  }, [inventoryData]);

  return (
    <Box>
      <StandardHeader />
      <Box p={5}>
        <StandardHeader
          title="Dashboard ยอดวัคซีนคงคลัง"
          bgColor="green.600"
          icon={faBoxOpen}
        /><br />

        {/* สรุปภาพรวม */}
        <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={4} mb={5}>
          <GridItem>
            <Box bg={cardBg} p={5} borderRadius="md" boxShadow="md" bgColor="green.50">
              <Stat>
                <StatLabel>
                  <HStack color="green.600">
                    <FontAwesomeIcon icon={faVial} />
                    <Text fontWeight="bold">ชนิดวัคซีน</Text>
                  </HStack>
                </StatLabel>
                <StatNumber color="green.600">
                  {inventorySummary.vaccineTypes || 0}
                </StatNumber>
                <StatHelpText>
                  ชนิด
                </StatHelpText>
              </Stat>
            </Box>
          </GridItem>
          <GridItem>
            <Box bg={cardBg} p={5} borderRadius="md" boxShadow="md" bgColor="green.50">
              <Stat>
                <StatLabel>
                  <HStack color="green.600">
                    <FontAwesomeIcon icon={faSyringe} />
                    <Text fontWeight="bold">จำนวนโดส</Text>
                  </HStack>
                </StatLabel>
                <StatNumber color="green.600">
                  {inventorySummary.totalDoses.toLocaleString("th-TH") || 0}
                </StatNumber>
                <StatHelpText>
                  โดส
                </StatHelpText>
              </Stat>
            </Box>
          </GridItem>
          <GridItem>
            <Box bg={cardBg} p={5} borderRadius="md" boxShadow="md" bgColor="green.50">
              <Stat>
                <StatLabel>
                  <HStack color="green.600">
                    <FontAwesomeIcon icon={faMoneyBillWave} />
                    <Text fontWeight="bold">จำนวนเงิน</Text>
                  </HStack>
                </StatLabel>
                <StatNumber color="green.600">
                  {inventorySummary.totalAmount.toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
                </StatNumber>
                <StatHelpText>
                  บาท
                </StatHelpText>
              </Stat>
            </Box>
          </GridItem>
        </Grid>

        {/* กราฟยอดวัคซีนคงคลัง */}
        <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={4} mb={5}>
          {/* กราฟภาพรวมจำนวนโดสทั้งหมด */}
          <GridItem>
            <Box bg={cardBg} p={5} borderRadius="md" boxShadow="md" border="1px solid" borderColor="green.500">
              <HStack justify="space-between" mb={4}>
                <Heading size="md" color="green.600">ยอดวัคซีนคงคลัง - จำนวนโดส</Heading>
                <IconButton.Readmore
                  size="sm"
                  backgroundColor="green.400"
                  onClick={handleViewAllInventoryDetails}
                  label="ดูรายละเอียด"
                />
              </HStack>
              
              {isLoading ? (
                <Center p={10}>
                  <Spinner size="xl" />
                </Center>
              ) : overallChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart 
                    data={overallChartData} 
                    margin={{ top: 20, right: 30, left: 20, bottom: 100 }}
                    onClick={(e) => {
                      console.log('BarChart onClick event:', e);
                      if (e && e.activePayload && e.activePayload.length > 0) {
                        const payload = e.activePayload[0].payload;
                        console.log('BarChart onClick payload:', payload);
                        if (payload && payload.regionID) {
                          console.log('Calling handleViewInventoryDetails with regionID:', payload.regionID);
                          handleViewInventoryDetails(payload.regionID);
                        } else {
                          console.warn('No regionID in payload:', payload);
                        }
                      }
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="regionName"
                      type="category"
                      angle={-45}
                      textAnchor="end"
                      interval={0}
                      height={120}
                      tick={(props) => {
                        const { x, y, payload } = props;
                        const entry = overallChartData.find(d => d.regionName === payload.value);
                        return (
                          <g transform={`translate(${x},${y})`}>
                            <text
                              x={0}
                              y={0}
                              dy={16}
                              textAnchor="end"
                              fill="#666"
                              fontSize={12}
                              transform="rotate(-45)"
                              style={{ cursor: 'pointer' }}
                              onClick={() => {
                                if (entry && entry.regionID) {
                                  console.log('XAxis tick onClick entry:', entry);
                                  handleViewInventoryDetails(entry.regionID);
                                }
                              }}
                            >
                              {payload.value}
                            </text>
                          </g>
                        );
                      }}
                    />
                    <YAxis
                      label={{
                        value: "จำนวนโดส",
                        angle: -90,
                        position: "insideLeft",
                        offset: -5,
                      }}
                      tick={{ fontSize: 12 }}
                      tickFormatter={(value) => value.toLocaleString("th-TH")}
                    />
                    <Tooltip 
                      content={<CustomTooltip />}
                      formatter={(value) => [value.toLocaleString("th-TH") + " โดส", "จำนวนโดส"]}
                    />
                    <Legend wrapperStyle={{ fontSize: '11px' }} iconSize={12} />
                    <Bar 
                      dataKey="totalDoses" 
                      name="จำนวนโดส" 
                      fill="#319795"
                      style={{ cursor: 'pointer' }}
                      radius={[8, 8, 0, 0]}
                    >
                      {overallChartData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill="#319795"
                          onClick={() => {
                            console.log('Cell onClick entry:', entry);
                            if (entry.regionID) {
                              console.log('Calling handleViewInventoryDetails with regionID:', entry.regionID);
                              handleViewInventoryDetails(entry.regionID);
                            } else {
                              console.warn('No regionID in entry:', entry);
                            }
                          }}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Center p={10}>
                  <Text color="gray.500">ไม่มีข้อมูล</Text>
                </Center>
              )}
            </Box>
          </GridItem>

          {/* กราฟภาพรวมจำนวนเงินทั้งหมด */}
          <GridItem>
            <Box bg={cardBg} p={5} borderRadius="md" boxShadow="md" border="1px solid" borderColor="green.500">
              <HStack justify="space-between" mb={4}>
                <Heading size="md" color="green.600">ยอดวัคซีนคงคลัง - จำนวนเงิน</Heading>
                <IconButton.Readmore
                  size="sm"
                  backgroundColor="green.400"
                  onClick={handleViewAllInventoryDetails}
                  label="ดูรายละเอียด"
                />
              </HStack>
              
              {isLoading ? (
                <Center p={10}>
                  <Spinner size="xl" />
                </Center>
              ) : overallChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart 
                    data={overallChartData} 
                    margin={{ top: 20, right: 30, left: 20, bottom: 100 }}
                    onClick={(e) => {
                      console.log('BarChart onClick event:', e);
                      if (e && e.activePayload && e.activePayload.length > 0) {
                        const payload = e.activePayload[0].payload;
                        console.log('BarChart onClick payload:', payload);
                        if (payload && payload.regionID) {
                          console.log('Calling handleViewInventoryDetails with regionID:', payload.regionID);
                          handleViewInventoryDetails(payload.regionID);
                        } else {
                          console.warn('No regionID in payload:', payload);
                        }
                      }
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="regionName"
                      type="category"
                      angle={-45}
                      textAnchor="end"
                      interval={0}
                      height={120}
                      tick={(props) => {
                        const { x, y, payload } = props;
                        const entry = overallChartData.find(d => d.regionName === payload.value);
                        return (
                          <g transform={`translate(${x},${y})`}>
                            <text
                              x={0}
                              y={0}
                              dy={16}
                              textAnchor="end"
                              fill="#666"
                              fontSize={12}
                              transform="rotate(-45)"
                              style={{ cursor: 'pointer' }}
                              onClick={() => {
                                if (entry && entry.regionID) {
                                  console.log('XAxis tick onClick entry:', entry);
                                  handleViewInventoryDetails(entry.regionID);
                                }
                              }}
                            >
                              {payload.value}
                            </text>
                          </g>
                        );
                      }}
                    />
                    <YAxis
                      label={{
                        value: "มูลค่า (บาท)",
                        angle: -90,
                        position: "insideLeft",
                        offset: -5,
                      }}
                      tick={{ fontSize: 12 }}
                      tickFormatter={(value) => value.toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    />
                    <Tooltip 
                      content={<CustomTooltip />}
                      formatter={(value) => [value.toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " บาท", "จำนวนเงิน"]}
                    />
                    <Legend wrapperStyle={{ fontSize: '11px' }} iconSize={12} />
                    <Bar 
                      dataKey="totalAmount" 
                      name="จำนวนเงิน"
                      fill="#319795"
                      style={{ cursor: 'pointer' }}
                      radius={[8, 8, 0, 0]}
                    >
                      {overallChartData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill="#319795"
                          onClick={() => {
                            console.log('Cell onClick entry:', entry);
                            if (entry.regionID) {
                              console.log('Calling handleViewInventoryDetails with regionID:', entry.regionID);
                              handleViewInventoryDetails(entry.regionID);
                            } else {
                              console.warn('No regionID in entry:', entry);
                            }
                          }}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Center p={10}>
                  <Text color="gray.500">ไม่มีข้อมูล</Text>
                </Center>
              )}
            </Box>
          </GridItem>

          {/* กราฟจำนวนโดส (แยกตามชนิดวัคซีน) */}
          <GridItem>
            <Box bg={cardBg} p={5} borderRadius="md" boxShadow="md" border="1px solid" borderColor="green.500">
              <Heading size="md" color="green.600" mb={4}>ยอดวัคซีนคงคลัง - จำนวนโดส (แยกตามชนิดวัคซีน)</Heading>
              
              {isLoading ? (
                <Center p={10}>
                  <Spinner size="xl" />
                </Center>
              ) : inventoryChartData.chartData.length > 0 && inventoryChartData.vaccineNames.length > 0 ? (
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart 
                    data={inventoryChartData.chartData} 
                    margin={{ top: 20, right: 30, left: 20, bottom: 100 }}
                    onClick={(e) => {
                      console.log('BarChart onClick event:', e);
                      if (e && e.activePayload && e.activePayload.length > 0) {
                        const payload = e.activePayload[0].payload;
                        console.log('BarChart onClick payload:', payload);
                        if (payload && payload.regionID) {
                          console.log('Calling handleViewInventoryDetails with regionID:', payload.regionID);
                          handleViewInventoryDetails(payload.regionID);
                        } else {
                          console.warn('No regionID in payload:', payload);
                        }
                      }
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="regionName"
                      type="category"
                      angle={-45}
                      textAnchor="end"
                      interval={0}
                      height={120}
                      tick={(props) => {
                        const { x, y, payload } = props;
                        const entry = overallChartData.find(d => d.regionName === payload.value);
                        return (
                          <g transform={`translate(${x},${y})`}>
                            <text
                              x={0}
                              y={0}
                              dy={16}
                              textAnchor="end"
                              fill="#666"
                              fontSize={12}
                              transform="rotate(-45)"
                              style={{ cursor: 'pointer' }}
                              onClick={() => {
                                if (entry && entry.regionID) {
                                  console.log('XAxis tick onClick entry:', entry);
                                  handleViewInventoryDetails(entry.regionID);
                                }
                              }}
                            >
                              {payload.value}
                            </text>
                          </g>
                        );
                      }}
                    />
                    <YAxis
                      label={{
                        value: "จำนวนโดส",
                        angle: -90,
                        position: "insideLeft",
                        offset: -5,
                      }}
                      tick={{ fontSize: 12 }}
                      tickFormatter={(value) => value.toLocaleString("th-TH")}
                    />
                    <Tooltip 
                      content={<CustomTooltip />}
                      formatter={(value, name) => {
                        if (name.includes('_doses')) {
                          return [value.toLocaleString("th-TH") + " โดส", name.replace('_doses', '')];
                        }
                        return [value, name];
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: '11px' }} iconSize={12} />
                    {inventoryChartData.vaccineNames.map((vaccineName, index) => {
                      const color = getVaccineColor(vaccineName, index);
                      return (
                        <Line
                          key={`${vaccineName}_doses`}
                          type="monotone"
                          dataKey={`${vaccineName}_doses`}
                          name={vaccineName}
                          stroke={color}
                          strokeWidth={2}
                          connectNulls={false}
                          dot={{ r: 4, cursor: 'pointer' }}
                          activeDot={{ r: 6, cursor: 'pointer' }}
                        />
                      );
                    })}
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <Center p={10}>
                  <Text color="gray.500">ไม่มีข้อมูล</Text>
                </Center>
              )}
              
              {/* Pie Chart - จำนวนโดส */}
              {pieChartData.doses.length > 0 && (
                <Box mt={5}>
                  <Heading size="sm" mb={3} color="green.700">5 อันดับแรก - จำนวนโดส</Heading>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={pieChartData.doses}
                        dataKey="doses"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        label={<CustomPieLabel />}
                        labelLine={true}
                      >
                        {pieChartData.doses.map((entry, index) => {
                          // หา index ของ vaccineName ใน inventoryChartData.vaccineNames เพื่อให้สีตรงกับ Line Chart
                          const vaccineIndex = inventoryChartData.vaccineNames.indexOf(entry.name);
                          const colorIndex = vaccineIndex >= 0 ? vaccineIndex : index;
                          return (
                            <Cell key={`cell-${index}`} fill={getVaccineColor(entry.name, colorIndex)} />
                          );
                        })}
                      </Pie>
                      <Tooltip content={<CustomPieTooltip />} />
                      <Legend wrapperStyle={{ fontSize: '11px' }} iconSize={12} />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              )}
            </Box>
          </GridItem>

          {/* กราฟจำนวนเงิน (แยกตามชนิดวัคซีน) */}
          <GridItem>
            <Box bg={cardBg} p={5} borderRadius="md" boxShadow="md" border="1px solid" borderColor="green.500">
              <Heading size="md" color="green.600" mb={4}>ยอดวัคซีนคงคลัง - จำนวนเงิน (แยกตามชนิดวัคซีน)</Heading>
              
              {isLoading ? (
                <Center p={10}>
                  <Spinner size="xl" />
                </Center>
              ) : inventoryChartData.chartData.length > 0 && inventoryChartData.vaccineNames.length > 0 ? (
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart 
                    data={inventoryChartData.chartData} 
                    margin={{ top: 20, right: 30, left: 20, bottom: 100 }}
                    onClick={(e) => {
                      console.log('BarChart onClick event:', e);
                      if (e && e.activePayload && e.activePayload.length > 0) {
                        const payload = e.activePayload[0].payload;
                        console.log('BarChart onClick payload:', payload);
                        if (payload && payload.regionID) {
                          console.log('Calling handleViewInventoryDetails with regionID:', payload.regionID);
                          handleViewInventoryDetails(payload.regionID);
                        } else {
                          console.warn('No regionID in payload:', payload);
                        }
                      }
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="regionName"
                      type="category"
                      angle={-45}
                      textAnchor="end"
                      interval={0}
                      height={120}
                      tick={(props) => {
                        const { x, y, payload } = props;
                        const entry = overallChartData.find(d => d.regionName === payload.value);
                        return (
                          <g transform={`translate(${x},${y})`}>
                            <text
                              x={0}
                              y={0}
                              dy={16}
                              textAnchor="end"
                              fill="#666"
                              fontSize={12}
                              transform="rotate(-45)"
                              style={{ cursor: 'pointer' }}
                              onClick={() => {
                                if (entry && entry.regionID) {
                                  console.log('XAxis tick onClick entry:', entry);
                                  handleViewInventoryDetails(entry.regionID);
                                }
                              }}
                            >
                              {payload.value}
                            </text>
                          </g>
                        );
                      }}
                    />
                    <YAxis
                      label={{
                        value: "มูลค่า (บาท)",
                        angle: -90,
                        position: "insideLeft",
                        offset: -5,
                      }}
                      tick={{ fontSize: 12 }}
                      tickFormatter={(value) => value.toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    />
                    <Tooltip 
                      content={<CustomTooltip />}
                      formatter={(value, name) => {
                        if (name.includes('_amount')) {
                          return [value.toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " บาท", name.replace('_amount', '')];
                        }
                        return [value, name];
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: '11px' }} iconSize={12} />
                    {inventoryChartData.vaccineNames.map((vaccineName, index) => {
                      const color = getVaccineColor(vaccineName, index);
                      return (
                        <Line
                          key={`${vaccineName}_amount`}
                          type="monotone"
                          dataKey={`${vaccineName}_amount`}
                          name={vaccineName}
                          stroke={color}
                          strokeWidth={2}
                          connectNulls={false}
                          dot={{ r: 4, cursor: 'pointer' }}
                          activeDot={{ r: 6, cursor: 'pointer' }}
                        />
                      );
                    })}
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <Center p={10}>
                  <Text color="gray.500">ไม่มีข้อมูล</Text>
                </Center>
              )}
              
              {/* Pie Chart - จำนวนเงิน */}
              {pieChartData.amounts.length > 0 && (
                <Box mt={5}>
                  <Heading size="sm" mb={3} color="green.700">5 อันดับแรก - จำนวนเงิน</Heading>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={pieChartData.amounts}
                        dataKey="amounts"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        label={<CustomPieLabel />}
                        labelLine={true}
                      >
                        {pieChartData.amounts.map((entry, index) => {
                          // หา index ของ vaccineName ใน inventoryChartData.vaccineNames เพื่อให้สีตรงกับ Line Chart
                          const vaccineIndex = inventoryChartData.vaccineNames.indexOf(entry.name);
                          const colorIndex = vaccineIndex >= 0 ? vaccineIndex : index;
                          return (
                            <Cell key={`cell-${index}`} fill={getVaccineColor(entry.name, colorIndex)} />
                          );
                        })}
                      </Pie>
                      <Tooltip 
                        content={<CustomPieTooltip />}
                        formatter={(value) => value.toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " บาท"}
                      />
                      <Legend wrapperStyle={{ fontSize: '11px' }} iconSize={12} />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              )}
            </Box>
          </GridItem>
        </Grid>
            
        {/* วัคซีนที่ใกล้หมดอายุ */}
        
        <StandardHeader
          title="วัคซีนที่ใกล้หมดอายุ"
          bgColor="orange.600"
          icon={faCalendarXmark}
        /><br />
        <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={4} mb={5}>
          <GridItem>
            <Box bg={cardBg} p={5} borderRadius="md" boxShadow="md" border="1px solid" borderColor="orange.500">
              <Heading size="md" color="orange.600" mb={4}>วัคซีนที่ใกล้หมดอายุ - ภายใน 60 วัน</Heading>
              {isLoading ? (
                <Center p={10}>
                  <Spinner size="xl" />
                </Center>
              ) : (
                <Box maxH="600px" overflowY="auto">
                  {(() => {
                    const filteredData = expiringVaccines.filter(item => item.ExpiryCategory === '60');
                    if (filteredData.length === 0) {
                      return (
                        <Center p={5}>
                          <Text color="gray.500">ไม่มีข้อมูล</Text>
                        </Center>
                      );
                    }

                    // จัดกลุ่มตาม VaccineID + LotID (ไม่รวมวัคซีนที่เหมือนกัน)
                    const groupedData = filteredData.reduce((acc, item) => {
                      // Debug: ตรวจสอบข้อมูลที่ได้รับ
                      if (!item.LotID || !item.ExpiryDate) {
                        console.warn("Missing data in item:", item);
                      }
                      
                      const key = `${item.VaccineID}_${item.LotID || 'UNKNOWN'}`;
                      
                      if (!acc[key]) {
                        acc[key] = {
                          VaccineID: item.VaccineID,
                          VaccineNameTH: item.VaccineNameTH,
                          VaccineNameEN: item.VaccineNameEN,
                          LotID: item.LotID || 'ไม่ระบุ',
                          ExpiryDate: item.ExpiryDate,
                          DaysUntilExpiry: item.DaysUntilExpiry !== null && item.DaysUntilExpiry !== undefined ? item.DaysUntilExpiry : null,
                          warehouses: [],
                          totalBottles: 0,
                          totalDoses: 0,
                          totalAmount: 0,
                        };
                      }
                      
                      // ตรวจสอบว่า warehouse นี้มีข้อมูลหรือไม่
                      const warehouseBottles = Number(item.TotalBottles || 0);
                      const warehouseDoses = Number(item.TotalDoses || 0);
                      const warehouseAmount = Number(item.TotalAmount || 0);
                      
                      if (warehouseBottles > 0 || warehouseDoses > 0 || warehouseAmount > 0) {
                        acc[key].warehouses.push({
                          WarehouseID: item.WarehouseID || 'ไม่ระบุ',
                          WarehouseName: item.WarehouseName || 'ไม่ระบุ',
                          RegionName: item.RegionName || (item.RegionKey === 'N/A' || item.RegionKey === null ? 'สำนักเทคโนโลยีชีวภัณฑ์สัตว์' : (item.RegionKey || 'ไม่ระบุ')),
                          TotalBottles: warehouseBottles,
                          TotalDoses: warehouseDoses,
                          TotalAmount: warehouseAmount,
                        });
                        
                        acc[key].totalBottles += warehouseBottles;
                        acc[key].totalDoses += warehouseDoses;
                        acc[key].totalAmount += warehouseAmount;
                      }
                      
                      return acc;
                    }, {});

                    // เรียงลำดับตาม VaccineID
                    const sortedData = Object.values(groupedData).sort((a, b) => {
                      if (a.VaccineID < b.VaccineID) return -1;
                      if (a.VaccineID > b.VaccineID) return 1;
                      return 0;
                    });

                    return (
                      <Accordion allowMultiple>
                        {sortedData.map((item, index) => (
                          <AccordionItem key={index} borderColor="orange.200">
                            <AccordionButton>
                              <Box flex="1" textAlign="left">
                                <HStack justify="space-between">
                                  <VStack align="start" spacing={1}>
                                    <Text fontWeight="bold" color="orange.700">
                                      {item.VaccineID} : {item.VaccineNameTH || item.VaccineNameEN || item.VaccineID}
                                    </Text>
                                    <Text fontSize="sm" color="orange.600">
                                      Lot No.: {item.LotID}
                                    </Text>
                                    <Text fontSize="xs" color="gray.500">
                                      หมดอายุ: {item.ExpiryDate ? new Date(item.ExpiryDate).toLocaleDateString('th-TH') : '-'} {item.DaysUntilExpiry !== null && item.DaysUntilExpiry !== undefined ? `(${item.DaysUntilExpiry} วัน)` : ''}
                                    </Text>
                                    <Text fontSize="sm" color="gray.600">
                                      ยอดรวม: ขวด {item.totalBottles.toLocaleString("th-TH")} | โดส {item.totalDoses.toLocaleString("th-TH")} | เงิน {item.totalAmount.toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} บาท
                                    </Text>
                                  </VStack>
                                </HStack>
                              </Box>
                              <AccordionIcon />
                            </AccordionButton>
                            <AccordionPanel pb={4}>
                              <TableContainer>
                                <Table variant="simple" size="sm">
                                  <Thead>
                                    <Tr>
                                      <Th>คลังวัคซีน</Th>
                                      <Th isNumeric>จำนวนขวด</Th>
                                      <Th isNumeric>จำนวนโดส</Th>
                                      <Th isNumeric>จำนวนเงิน (บาท)</Th>
                                    </Tr>
                                  </Thead>
                                  <Tbody>
                                    {item.warehouses.map((warehouse, wIndex) => (
                                      <Tr key={wIndex}>
                                        <Td>{warehouse.WarehouseName} {warehouse.RegionName && `(${warehouse.RegionName})`}</Td>
                                        <Td isNumeric>{warehouse.TotalBottles.toLocaleString("th-TH")}</Td>
                                        <Td isNumeric>{warehouse.TotalDoses.toLocaleString("th-TH")}</Td>
                                        <Td isNumeric>{warehouse.TotalAmount.toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Td>
                                      </Tr>
                                    ))}
                                  </Tbody>
                                </Table>
                              </TableContainer>
                            </AccordionPanel>
                          </AccordionItem>
                        ))}
                      </Accordion>
                    );
                  })()}
                </Box>
              )}
            </Box>
          </GridItem>
          <GridItem>
            <Box bg={cardBg} p={5} borderRadius="md" boxShadow="md" border="1px solid" borderColor="orange.500">
              <Heading size="md" color="orange.600" mb={4}>วัคซีนที่ใกล้หมดอายุ - ภายใน 61 - 120 วัน</Heading>
              {isLoading ? (
                <Center p={10}>
                  <Spinner size="xl" />
                </Center>
              ) : (
                <Box maxH="600px" overflowY="auto">
                  {(() => {
                    // กรองข้อมูลที่เหลืออายุ 61-120 วัน (ไม่รวม 60 วัน เพื่อไม่ให้ซ้ำ)
                    const filteredData = expiringVaccines.filter(item => {
                      const days = item.DaysUntilExpiry;
                      return item.ExpiryCategory === '120' && days !== null && days !== undefined && days > 60;
                    });
                    if (filteredData.length === 0) {
                      return (
                        <Center p={5}>
                          <Text color="gray.500">ไม่มีข้อมูล</Text>
                        </Center>
                      );
                    }

                    // จัดกลุ่มตาม VaccineID + LotID (ไม่รวมวัคซีนที่เหมือนกัน)
                    const groupedData = filteredData.reduce((acc, item) => {
                      // Debug: ตรวจสอบข้อมูลที่ได้รับ
                      if (!item.LotID || !item.ExpiryDate) {
                        console.warn("Missing data in item:", item);
                      }
                      
                      const key = `${item.VaccineID}_${item.LotID || 'UNKNOWN'}`;
                      
                      if (!acc[key]) {
                        acc[key] = {
                          VaccineID: item.VaccineID,
                          VaccineNameTH: item.VaccineNameTH,
                          VaccineNameEN: item.VaccineNameEN,
                          LotID: item.LotID || 'ไม่ระบุ',
                          ExpiryDate: item.ExpiryDate,
                          DaysUntilExpiry: item.DaysUntilExpiry !== null && item.DaysUntilExpiry !== undefined ? item.DaysUntilExpiry : null,
                          warehouses: [],
                          totalBottles: 0,
                          totalDoses: 0,
                          totalAmount: 0,
                        };
                      }
                      
                      // ตรวจสอบว่า warehouse นี้มีข้อมูลหรือไม่
                      const warehouseBottles = Number(item.TotalBottles || 0);
                      const warehouseDoses = Number(item.TotalDoses || 0);
                      const warehouseAmount = Number(item.TotalAmount || 0);
                      
                      if (warehouseBottles > 0 || warehouseDoses > 0 || warehouseAmount > 0) {
                        acc[key].warehouses.push({
                          WarehouseID: item.WarehouseID || 'ไม่ระบุ',
                          WarehouseName: item.WarehouseName || 'ไม่ระบุ',
                          RegionName: item.RegionName || (item.RegionKey === 'N/A' || item.RegionKey === null ? 'สำนักเทคโนโลยีชีวภัณฑ์สัตว์' : (item.RegionKey || 'ไม่ระบุ')),
                          TotalBottles: warehouseBottles,
                          TotalDoses: warehouseDoses,
                          TotalAmount: warehouseAmount,
                        });
                        
                        acc[key].totalBottles += warehouseBottles;
                        acc[key].totalDoses += warehouseDoses;
                        acc[key].totalAmount += warehouseAmount;
                      }
                      
                      return acc;
                    }, {});

                    // เรียงลำดับตาม VaccineID
                    const sortedData = Object.values(groupedData).sort((a, b) => {
                      if (a.VaccineID < b.VaccineID) return -1;
                      if (a.VaccineID > b.VaccineID) return 1;
                      return 0;
                    });

                    return (
                      <Accordion allowMultiple>
                        {sortedData.map((item, index) => (
                          <AccordionItem key={index} borderColor="orange.200">
                            <AccordionButton>
                              <Box flex="1" textAlign="left">
                                <HStack justify="space-between">
                                  <VStack align="start" spacing={1}>
                                    <Text fontWeight="bold" color="orange.700">
                                      {item.VaccineID} : {item.VaccineNameTH || item.VaccineNameEN || item.VaccineID}
                                    </Text>
                                    <Text fontSize="sm" color="orange.600">
                                      Lot No.: {item.LotID}
                                    </Text>
                                    <Text fontSize="xs" color="gray.500">
                                      หมดอายุ: {item.ExpiryDate ? new Date(item.ExpiryDate).toLocaleDateString('th-TH') : '-'} {item.DaysUntilExpiry !== null && item.DaysUntilExpiry !== undefined ? `(${item.DaysUntilExpiry} วัน)` : ''}
                                    </Text>
                                    <Text fontSize="sm" color="gray.600">
                                      ยอดรวม: ขวด {item.totalBottles.toLocaleString("th-TH")} | โดส {item.totalDoses.toLocaleString("th-TH")} | เงิน {item.totalAmount.toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} บาท
                                    </Text>
                                  </VStack>
                                </HStack>
                              </Box>
                              <AccordionIcon />
                            </AccordionButton>
                            <AccordionPanel pb={4}>
                              <TableContainer>
                                <Table variant="simple" size="sm">
                                  <Thead>
                                    <Tr>
                                      <Th>คลังวัคซีน</Th>
                                      <Th isNumeric>จำนวนขวด</Th>
                                      <Th isNumeric>จำนวนโดส</Th>
                                      <Th isNumeric>จำนวนเงิน (บาท)</Th>
                                    </Tr>
                                  </Thead>
                                  <Tbody>
                                    {item.warehouses.map((warehouse, wIndex) => (
                                      <Tr key={wIndex}>
                                        <Td>{warehouse.WarehouseName} {warehouse.RegionName && `(${warehouse.RegionName})`}</Td>
                                        <Td isNumeric>{warehouse.TotalBottles.toLocaleString("th-TH")}</Td>
                                        <Td isNumeric>{warehouse.TotalDoses.toLocaleString("th-TH")}</Td>
                                        <Td isNumeric>{warehouse.TotalAmount.toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Td>
                                      </Tr>
                                    ))}
                                  </Tbody>
                                </Table>
                              </TableContainer>
                            </AccordionPanel>
                          </AccordionItem>
                        ))}
                      </Accordion>
                    );
                  })()}
                </Box>
              )}
            </Box>
          </GridItem>
        </Grid>

        {/* Modal สำหรับแสดงรายละเอียด */}
        <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} size="6xl">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>{modalTitle}</ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              <Box mb={4} p={4} bg="gray.50" borderRadius="md">
                <FormControl>
                  <FormLabel fontSize="sm">ค้นหาคลัง</FormLabel>
                  <Input
                    value={modalFilter.warehouseName}
                    onChange={(e) => setModalFilter({ ...modalFilter, warehouseName: e.target.value })}
                    placeholder="พิมพ์เพื่อค้นหา..."
                    size="sm"
                  />
                </FormControl>
              </Box>
              {isLoadingDetails ? (
                <Center p={10}>
                  <Spinner size="xl" />
                </Center>
              ) : inventoryDetails.length > 0 ? (
                <Accordion allowMultiple>
                  {inventoryDetails
                    .filter(item => 
                      !modalFilter.warehouseName || 
                      item.WarehouseID?.toLowerCase().includes(modalFilter.warehouseName.toLowerCase()) ||
                      item.WarehouseName?.toLowerCase().includes(modalFilter.warehouseName.toLowerCase()) ||
                      item.RegionID?.toLowerCase().includes(modalFilter.warehouseName.toLowerCase()) ||
                      item.RegionName?.toLowerCase().includes(modalFilter.warehouseName.toLowerCase())
                    )
                    .reduce((acc, item) => {
                      // ถ้าเป็นรายละเอียดทั้งหมด (มี RegionID) ให้จัดกลุ่มตาม Region แล้ว Warehouse
                      if (item.RegionID !== null && item.RegionID !== undefined) {
                        // แปลง RegionID เป็น key (ถ้าเป็น null หรือ undefined หรือ 'N/A' ให้ใช้ 'WHH100')
                        const regionKey = (!item.RegionID || item.RegionID === 'N/A') ? 'WHH100' : item.RegionID;
                        let regionGroup = acc.find(x => {
                          const xKey = (!x.RegionID || x.RegionID === 'N/A') ? 'WHH100' : x.RegionID;
                          return xKey === regionKey;
                        });
                        if (!regionGroup) {
                          // ถ้า RegionID เป็น null หรือ 'N/A' ให้ใช้ชื่อ "สำนักเทคโนโลยีชีวภัณฑ์สัตว์"
                          const regionName = (!item.RegionID || item.RegionID === 'N/A')
                            ? 'สำนักเทคโนโลยีชีวภัณฑ์สัตว์'
                            : (item.RegionName || item.RegionID);
                          regionGroup = {
                            RegionID: (!item.RegionID || item.RegionID === 'N/A') ? 'WHH100' : item.RegionID,
                            RegionName: regionName,
                            warehouses: [],
                          };
                          acc.push(regionGroup);
                        }
                        
                        let warehouse = regionGroup.warehouses.find(w => w.WarehouseID === item.WarehouseID);
                        if (!warehouse) {
                          warehouse = {
                            WarehouseID: item.WarehouseID,
                            WarehouseName: item.WarehouseName,
                            vaccines: [],
                          };
                          regionGroup.warehouses.push(warehouse);
                        }
                        warehouse.vaccines.push(item);
                      } else {
                        // รูปแบบเดิม (ไม่มี RegionID)
                        const existing = acc.find(x => x.WarehouseID === item.WarehouseID);
                        if (existing) {
                          existing.vaccines.push(item);
                        } else {
                          acc.push({
                            WarehouseID: item.WarehouseID,
                            WarehouseName: item.WarehouseName,
                            vaccines: [item],
                          });
                        }
                      }
                      return acc;
                    }, [])
                    .map((group, index) => {
                      // ถ้าเป็นรายละเอียดทั้งหมด (มี RegionID)
                      if (group.RegionID !== null && group.RegionID !== undefined) {
                        // แสดงชื่อ Region (ถ้าเป็น null หรือ 'N/A' หรือ 'WHH100' ให้แสดง "สำนักเทคโนโลยีชีวภัณฑ์สัตว์")
                        const displayRegionName = (!group.RegionID || group.RegionID === 'N/A' || group.RegionID === 'WHH100')
                          ? 'สำนักเทคโนโลยีชีวภัณฑ์สัตว์'
                          : (group.RegionName || group.RegionID);
                        
                        return (
                          <AccordionItem key={`region-${index}`}>
                            <AccordionButton>
                              <Box flex="1" textAlign="left">
                                <HStack spacing={4}>
                                  <Text fontWeight="bold" color="green.600">{displayRegionName}</Text>
                                  <Text color="gray.500">({group.warehouses.length} คลัง)</Text>
                                </HStack>
                              </Box>
                              {group.warehouses.length > 0 && <AccordionIcon />}
                            </AccordionButton>
                            {group.warehouses.length > 0 && (
                              <AccordionPanel pb={4}>
                                <Accordion allowMultiple>
                                  {group.warehouses.map((warehouse, wIndex) => {
                                    const totalDoses = warehouse.vaccines.reduce((sum, v) => sum + (Number(v.TotalDoses) || 0), 0);
                                    const totalAmount = warehouse.vaccines.reduce((sum, v) => sum + (Number(v.TotalAmount) || 0), 0);
                                    return (
                                      <AccordionItem key={`warehouse-${wIndex}`}>
                                        <AccordionButton>
                                          <Box flex="1" textAlign="left">
                                            <HStack spacing={4}>
                                              <Text fontWeight="bold">{warehouse.WarehouseID || "-"}</Text>
                                              <Text>{warehouse.WarehouseName || "-"}</Text>
                                              <Text color="blue.600">โดส: {parseFloat(totalDoses).toLocaleString("th-TH")}</Text>
                                              <Text color="green.600">เงิน: {parseFloat(totalAmount).toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} บาท</Text>
                                            </HStack>
                                          </Box>
                                          {warehouse.vaccines.length > 0 && <AccordionIcon />}
                                        </AccordionButton>
                                        {warehouse.vaccines.length > 0 && (
                                          <AccordionPanel pb={4}>
                                            <TableContainer>
                                              <Table variant="simple" size="sm">
                                                <Thead>
                                                  <Tr>
                                                    <Th>รหัสวัคซีน</Th>
                                                    <Th>ชื่อวัคซีน</Th>
                                                    <Th>จำนวนขวด</Th>
                                                    <Th>จำนวนโดส</Th>
                                                    <Th>จำนวนเงิน (บาท)</Th>
                                                  </Tr>
                                                </Thead>
                                                <Tbody>
                                                  {warehouse.vaccines.map((vaccine, vIndex) => (
                                                    <Tr key={vIndex}>
                                                      <Td>{vaccine.VaccineID || "-"}</Td>
                                                      <Td>{vaccine.VaccineNameTH || vaccine.VaccineNameEN || "-"}</Td>
                                                      <Td>{parseFloat(vaccine.TotalBottles || 0).toLocaleString("th-TH")}</Td>
                                                      <Td>{parseFloat(vaccine.TotalDoses || 0).toLocaleString("th-TH")}</Td>
                                                      <Td>{parseFloat(vaccine.TotalAmount || 0).toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Td>
                                                    </Tr>
                                                  ))}
                                                </Tbody>
                                              </Table>
                                            </TableContainer>
                                          </AccordionPanel>
                                        )}
                                      </AccordionItem>
                                    );
                                  })}
                                </Accordion>
                              </AccordionPanel>
                            )}
                          </AccordionItem>
                        );
                      } else {
                        // รูปแบบเดิม (ไม่มี RegionID)
                        const totalDoses = group.vaccines.reduce((sum, v) => sum + (Number(v.TotalDoses) || 0), 0);
                        const totalAmount = group.vaccines.reduce((sum, v) => sum + (Number(v.TotalAmount) || 0), 0);
                        return (
                          <AccordionItem key={index}>
                            <AccordionButton>
                              <Box flex="1" textAlign="left">
                                <HStack spacing={4}>
                                  <Text fontWeight="bold">{group.WarehouseID || "-"}</Text>
                                  <Text>{group.WarehouseName || "-"}</Text>
                                  <Text color="blue.600">โดส: {parseFloat(totalDoses).toLocaleString("th-TH")}</Text>
                                  <Text color="green.600">เงิน: {parseFloat(totalAmount).toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} บาท</Text>
                                </HStack>
                              </Box>
                              {group.vaccines.length > 0 && <AccordionIcon />}
                            </AccordionButton>
                            {group.vaccines.length > 0 && (
                              <AccordionPanel pb={4}>
                                <TableContainer>
                                  <Table variant="simple" size="sm">
                                    <Thead>
                                      <Tr>
                                        <Th>รหัสวัคซีน</Th>
                                        <Th>ชื่อวัคซีน</Th>
                                        <Th>จำนวนขวด</Th>
                                        <Th>จำนวนโดส</Th>
                                        <Th>จำนวนเงิน (บาท)</Th>
                                      </Tr>
                                    </Thead>
                                    <Tbody>
                                      {group.vaccines.map((vaccine, vIndex) => (
                                        <Tr key={vIndex}>
                                          <Td>{vaccine.VaccineID || "-"}</Td>
                                          <Td>{vaccine.VaccineNameTH || vaccine.VaccineNameEN || "-"}</Td>
                                          <Td>{parseFloat(vaccine.TotalBottles || 0).toLocaleString("th-TH")}</Td>
                                          <Td>{parseFloat(vaccine.TotalDoses || 0).toLocaleString("th-TH")}</Td>
                                          <Td>{parseFloat(vaccine.TotalAmount || 0).toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Td>
                                        </Tr>
                                      ))}
                                    </Tbody>
                                  </Table>
                                </TableContainer>
                              </AccordionPanel>
                            )}
                          </AccordionItem>
                        );
                      }
                    })}
                </Accordion>
              ) : (
                <Center p={10}>
                  <Text color="gray.500">ไม่มีข้อมูล</Text>
                </Center>
              )}
            </ModalBody>
          </ModalContent>
        </Modal>
      </Box>
    </Box>
  );
};

export default VaccineInventoryDashboard;

